import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Container, Card, Flex, Text, Heading, Spinner, Button, Code } from "@radix-ui/themes";
import { CheckCircleIcon, AlertCircleIcon, CopyIcon } from "lucide-react";
import { config } from "@/config/env";
import toast from "react-hot-toast";

function TokenRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Flex justify="between" align="center" mb="1">
        <Text size="1" weight="medium" color="gray">
          {label}
        </Text>
        <Button
          size="1"
          variant="ghost"
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success(`${label} copied`);
          }}
        >
          <CopyIcon size={12} /> Copy
        </Button>
      </Flex>
      <Code
        size="1"
        style={{
          display: "block",
          padding: 8,
          wordBreak: "break-all",
          maxHeight: 80,
          overflow: "auto",
        }}
      >
        {value}
      </Code>
    </Box>
  );
}

export default function SocialCallback() {
  const { orgId, provider } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState("");
  const [issuedTokens, setIssuedTokens] = useState<{
    access_token: string;
    refresh_token: string;
  } | null>(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      const authCode = searchParams.get("code");
      const state = searchParams.get("state");

      if (!authCode || !orgId || !provider) {
        setError("Missing authorization code or parameters");
        setStatus("error");
        return;
      }

      try {
        let tokens: { access_token: string; refresh_token: string };

        if (state) {
          const response = await fetch(
            `${config.instanceUrl}/org/${orgId}/auth/v1/${provider}/callback?code=${encodeURIComponent(authCode)}&state=${encodeURIComponent(state)}`,
            { headers: { "Accept": "application/json" } }
          );

          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `Authentication failed (${response.status})`);
          }

          tokens = await response.json();
        } else {
          // Apple form_post: backend redirects here with a one-time code instead
          // of full tokens, so we exchange it.
          const response = await fetch(
            `${config.instanceUrl}/org/${orgId}/auth/v1/exchange`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", "Accept": "application/json" },
              body: JSON.stringify({ code: authCode }),
            }
          );

          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `Token exchange failed (${response.status})`);
          }

          tokens = await response.json();
        }

        if (tokens.access_token && tokens.refresh_token) {
          setStatus("success");
          setIssuedTokens({
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
          });
        } else {
          throw new Error("No tokens received from server");
        }
      } catch (err: any) {
        setError(err.message || "Authentication failed");
        setStatus("error");
      }
    };

    handleCallback();
  }, [orgId, provider, searchParams]);

  return (
    <Container size="1" style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", padding: "1rem",
    }}>
      <Card size="4" style={{ width: "100%", maxWidth: 420 }}>
        <Flex direction="column" align="center" gap="4" p="4">
          {status === "loading" && (
            <>
              <Spinner size="3" />
              <Heading size="4">Signing you in...</Heading>
              <Text size="2" color="gray">Completing {provider} authentication</Text>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircleIcon size={48} color="var(--green-9)" />
              <Heading size="4">Signed in successfully</Heading>
              <Text size="2" color="gray" align="center">
                {provider} sign-in works. Tokens issued for the project user
                below — drop them into Postman, your mobile app, or any client
                that needs to call the Anythink API.
              </Text>
              {issuedTokens && (
                <Flex direction="column" gap="2" style={{ width: "100%" }}>
                  <TokenRow label="Access token" value={issuedTokens.access_token} />
                  <TokenRow label="Refresh token" value={issuedTokens.refresh_token} />
                </Flex>
              )}
            </>
          )}
          {status === "error" && (
            <>
              <AlertCircleIcon size={48} color="var(--red-9)" />
              <Heading size="4">Sign-in failed</Heading>
              <Text size="2" color="red">{error}</Text>
              <Box mt="2">
                <a href={`/org/${orgId}/auth/login`} style={{ color: "var(--accent-9)" }}>
                  Back to login
                </a>
              </Box>
            </>
          )}
        </Flex>
      </Card>
    </Container>
  );
}
